import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import { useCallback, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Button,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import MainTable from "../../../../layouts/Table/MainTable";
import {
  IconCheck,
  IconExternalLink,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { Link, useNavigate, useParams } from "react-router-dom";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import {
  getRowClasses,
  minimizedSize,
} from "../../lists/ListingWithContent/listing";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import {
  copyForm,
  deleteForm,
  editForm,
  getForms,
} from "../../../../api/FormsAPI";
import DomainsCell from "../../resellers/DomainsCell";
import ItemDeleter from "../../../../components/TableComponents/ItemDeleter";
import { useLanguage } from "../../../../stores/LanguageStore";
import DuplicateRessource from "../../../../components/View/DuplicateRessource";
import PagePermission from "../../../../components/hooks/usePagePermission";

const FormsListing = () => {
  const lang = useLanguage((s) => s.language);
  const { formId } = useParams();
  const apiRef = useGridApiRef();
  const form_id = useMemo(() => parseInt(formId) || null, [formId]);

  const columns = useMemo(() => {
    return [
      numberColumn(),
      nameColumn({
        handleClick: () => {},
        disableLink: true,
        editFunction: () => editForm,
        getDisabled: () => false,
        apiRef,
        width: 350,
        rightSection: (row) => (
          <Group gap={4} wrap="nowrap">
            <Anchor target={"_blank"} href={`https://${row?.domain}`}>
              <ActionIcon variant="subtle">
                <IconExternalLink size={17} />
              </ActionIcon>
            </Anchor>
          </Group>
        ),
      }),
      {
        field: "domain",
        headerName: lang.forms.domain,
        width: 250,
        renderCell: ({ row, value }) => (
          <DomainsCell
            status={row.domain_info?.status}
            value={value}
            preferEmpty
          />
        ),
      },

      {
        field: "is_ready",
        headerName: lang.forms.is_ready,
        width: 100,
        renderCell: ({ row, value }) =>
          value ? (
            <ThemeIcon variant="light" color="green">
              <IconCheck />
            </ThemeIcon>
          ) : (
            <ThemeIcon variant="light" color="red">
              <IconX />
            </ThemeIcon>
          ),
      },
      dateColumn(),
    ];
  }, [apiRef]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [highlight, setHighlight] = useState(null);

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    navigate(`/forms/${row.id}`);
  };

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <DuplicateRessource
            current={row}
            onFinish={() => setLoading(true)}
            closeMenu={() => {}}
            handler={copyForm}
            responseField={"form_id"}
            isIcon
          />

          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteForm}
            deletedMessage={lang.forms.deleted}
            id={row.id}
            apiRef={apiRef}
          />
        </Group>
      );
    },
    [apiRef, lang.forms.deleted]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteForm}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />
      </>
    ),
    [apiRef]
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: getForms,
    responseField: "forms_list",
    setRows,
  });

  return (
    <Stack flex={1} px={48} pt={16} gap={0} h={"calc(100vh - 64px)"}>
      <PagePermission permission={"displayForms"} />
      <Group gap={"xl"}>
        <Text size="xl" fw={600} mt={16} mb={8} mr={"auto"}>
          {lang.forms.title}
        </Text>

        <Button
          leftSection={<IconPlus size={18} />}
          size="md"
          radius={"md"}
          fz={16}
          color="primary"
          component={Link}
          to={"/forms/create"}
        >
          {lang.forms.create_form}
        </Button>
      </Group>

      <Divider my={16} />
      <Flex flex={1}>
        <Group
          flex={1}
          wrap="nowrap"
          align="stretch"
          gap={0}
          style={{ overflowX: "hidden" }}
          pt={64}
          mt={-64}
        >
          <Flex
            flex={1}
            maw={form_id ? minimizedSize - 48 : null}
            pos={"relative"}
          >
            <NoItemsModal
              rows={rows}
              loading={loading}
              text={
                <Text size="xl" mb={32}>
                  {lang.forms.no_form}
                </Text>
              }
              button={
                <Button
                  leftSection={<IconPlus size={18} />}
                  size="md"
                  radius={"md"}
                  fz={16}
                  color="primary"
                  component={Link}
                  to={"/forms/create"}
                >
                  {lang.forms.create_form}
                </Button>
              }
            />
            <MainTable
              apiRef={apiRef}
              columns={columns}
              rows={rows}
              loading={loading}
              setLoading={setLoading}
              id={"model-table"}
              toolbarActions={form_id ? null : toolbarActions}
              actionsCell={actionsCell}
              onRowClick={handleRowClick}
              defaultHighlight={highlight}
              setDefaultHighlight={setHighlight}
              whiteButtons={!form_id}
              actionsCellWidth={100}
              disableGotoPage={form_id}
              disableVisibleColumns={form_id}
              defaultPerPage={15}
              disablePerPage={form_id}
              disableCheckboxes={!!form_id}
              getRowClassName={(params) =>
                getRowClasses({ params, mainId: formId, integerId: form_id })
              }
              withSorter={!!form_id}
              tableSx={{
                "& .MuiDataGrid-columnHeaders": {
                  border: "none !important",
                  display: form_id ? "none" : "flex",
                },
                "& .MuiDataGrid-cell": {
                  border: "none",
                },
                "& .MuiDataGrid-main": {
                  background: "var(--mantine-color-primary-2)",
                  border: "none !important",
                  borderRadius: 3,
                },
                "& .table-search": {
                  width: form_id ? "200px !important" : "300px !important",
                },
                "& .MuiDataGrid-footerContainer": {
                  border: "none !important",
                },
                "& .table-row": {
                  cursor: "pointer",
                  borderRadius: 3,
                  marginBottom: 1,
                  background: "#fff",
                  border: "1px solid var(--mantine-color-primary-1)",
                },
                "& .MuiDataGrid-pinnedColumnHeaders": {
                  background: "transparent",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right .table-row": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderLeft: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left .table-row": {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
                  borderRadius: form_id ? 3 : 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                "& .MuiDataGrid-selectedRowCount": {
                  visibility: form_id ? "hidden" : "visible",
                },
                "& .MuiPagination-root": {
                  left: form_id ? 0 : undefined,
                  transform: form_id ? "none" : undefined,
                },
              }}
            />
          </Flex>
        </Group>
      </Flex>
    </Stack>
  );
};

export default FormsListing;
